import { get } from "lodash";
import moment from "moment";
import { Link, useNavigate } from "react-router-dom";
import { EyeIcon, PencilIcon } from "@heroicons/react/outline";
import { useIsMobile } from "routes/customerAccount/hooks/useIsMobile";
import { CustomerAccountType, PagingType } from "routes/customerAccount/types";
import { isHaveActionPermission } from "utills/helpers";
import Button from "components/button";
import Pagination from "components/pagination";

interface PropType {
  data: CustomerAccountType[];
  paging: PagingType | null;
  handleChangePage: (page: number) => void;
}

const DataTable = (props: PropType) => {
  const { data, paging, handleChangePage } = props;

  const PAGE_SIZE = 10;

  const isMobile = useIsMobile(1024);
  const navigate = useNavigate();

  const handleformatDate = (value: string) => {
    const m = moment(value, "YYYY-MM-DD HH:mm:ss", true); // strict
    return m.isValid() ? m.format("DD-MM-YYYY HH:mm:ss") : "";
  };

  const handleFormatCreatedBy = (value: string) => {
    if (value === "user") return "User";
    if (value === "system") return "System";

    return value;
  };

  return (
    <section className="box-container flex flex-col gap-5">
      {isMobile ? (
        <div className="mb-6 w-full flex flex-col gap-[8px]">
          {data?.map((item: CustomerAccountType, index: number) => {
            const lastLoginAt = handleformatDate(
              get(item, "last_login_at", "")
            );
            const createdAt = handleformatDate(get(item, "created_at", ""));
            const updatedAt = handleformatDate(get(item, "updated_at", ""));

            return (
              <div
                key={index}
                className="w-full bg-white p-[16px] rounded-[8px] shadow-md"
              >
                <div className="w-full flex items-center justify-between">
                  <div className="opacity-0">action</div>

                  <div className="flex items-center gap-2">
                    {isHaveActionPermission(
                      "",
                      "customer_accounts",
                      "view"
                    ) && (
                      <Button
                        className="w-8 h-8 bg-gray-200 hover:bg-gray-100 active:bg-gray-300"
                        onClick={() => {
                          navigate(
                            `/customer-accounts/detail/${get(item, "code", "")}`
                          );
                        }}
                      >
                        <EyeIcon className="w-4 h-4 text-black" />
                      </Button>
                    )}
                  </div>
                </div>

                <div className="w-full mt-[16px] flex flex-col gap-[8px]">
                  <div className="w-full flex">
                    <span className="w-[30%] text-[14px] font-[500] text-[#262626]">
                      No
                    </span>
                    <span className="w-[70%] text-[14px] font-[400] text-[#2B3033] text-right">
                      {(get(paging, "current_page", 1) - 1) * PAGE_SIZE +
                        1 +
                        index}
                    </span>
                  </div>

                  <div className="w-full flex">
                    <span className="w-[30%] text-[14px] font-[500] text-[#262626]">
                      Customer Code
                    </span>
                    <Link
                      to={`/customer-accounts/detail/${get(item, "code", 0)}`}
                      className="w-[70%] text-[14px] font-medium text-brand-500 hover:underline break-words text-right"
                    >
                      {get(item, "code", 0)}
                    </Link>
                  </div>

                  <div className="w-full flex">
                    <span className="w-[30%] text-[14px] font-[500] text-[#262626]">
                      Full Name
                    </span>
                    <span className="w-[70%] text-[14px] font-[400] text-[#2B3033] text-right">
                      {get(item, "full_name", "")}
                    </span>
                  </div>

                  <div className="w-full flex">
                    <span className="w-[30%] text-[14px] font-[500] text-[#262626]">
                      Email
                    </span>
                    <span className="w-[70%] text-[14px] font-[400] text-[#2B3033] text-right">
                      {get(item, "email", "")}
                    </span>
                  </div>

                  <div className="w-full flex">
                    <span className="w-[30%] text-[14px] font-[500] text-[#262626]">
                      Phone
                    </span>
                    <span className="w-[70%] text-[14px] font-[400] text-[#2B3033] text-right">
                      {get(item, "phone", "")}
                    </span>
                  </div>

                  <div className="w-full flex">
                    <span className="w-[30%] text-[14px] font-[500] text-[#262626]">
                      Last Login At
                    </span>
                    <span className="w-[70%] text-[14px] font-[400] text-[#2B3033] text-right">
                      {lastLoginAt}
                    </span>
                  </div>

                  <div className="w-full flex">
                    <span className="w-[30%] text-[14px] font-[500] text-[#262626]">
                      Created At
                    </span>
                    <span className="w-[70%] text-[14px] font-[400] text-[#2B3033] text-right">
                      {createdAt}
                    </span>
                  </div>

                  <div className="w-full flex">
                    <span className="w-[30%] text-[14px] font-[500] text-[#262626]">
                      Updated At
                    </span>
                    <span className="w-[70%] text-[14px] font-[400] text-[#2B3033] text-right">
                      {updatedAt}
                    </span>
                  </div>

                  <div className="w-full flex">
                    <span className="w-[30%] text-[14px] font-[500] text-[#262626]">
                      Created By
                    </span>
                    <span className="w-[70%] text-[14px] font-[400] text-[#2B3033] text-right">
                      {handleFormatCreatedBy(get(item, "created_by", ""))}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="flow-root">
          <div className="relative overflow-x-auto min-w-full">
            <table className="mb-6 w-full divide-y divide-gray-300 bg-magnolia-500 rounded-md">
              <thead>
                <tr>
                  <th
                    scope="col"
                    className="th py-3.5 pl-4 pr-3 sm:pl-6 lg:pl-8"
                  >
                    No
                  </th>

                  <th scope="col" className="th">
                    Customer Code
                  </th>

                  <th scope="col" className="th">
                    Full Name
                  </th>

                  <th scope="col" className="th">
                    Email
                  </th>

                  <th scope="col" className="th">
                    Phone
                  </th>

                  <th scope="col" className="th">
                    Last Login At
                  </th>

                  <th scope="col" className="th">
                    Created At
                  </th>

                  <th scope="col" className="th">
                    Updated At
                  </th>

                  <th scope="col" className="th">
                    Created By
                  </th>

                  <th scope="col" className="th w-28">
                    <span className="sr-only">Action</span>
                    <p className="">Action</p>
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-200 bg-white">
                {data?.map((item: CustomerAccountType, index: number) => {
                  const lastLoginAt = handleformatDate(
                    get(item, "last_login_at", "")
                  );
                  const createdAt = handleformatDate(
                    get(item, "created_at", "")
                  );
                  const updatedAt = handleformatDate(
                    get(item, "updated_at", "")
                  );

                  return (
                    <tr key={index}>
                      <td className="td py-4 pl-4 pr-3 sm:pl-6 lg:pl-8">
                        {(get(paging, "current_page", 1) - 1) * PAGE_SIZE +
                          1 +
                          index}
                      </td>

                      <td className="td min-w-40 break-words">
                        <Link
                          to={`/customer-accounts/detail/${get(
                            item,
                            "code",
                            0
                          )}`}
                          className="w-fit font-medium text-brand-500 hover:underline"
                        >
                          {get(item, "code", 0)}
                        </Link>
                      </td>

                      <td className="td min-w-[230px]">
                        {get(item, "full_name", "")}
                      </td>

                      <td className="td min-w-40">{get(item, "email", "")}</td>

                      <td className="td min-w-[160px]">
                        (+{get(item, "phone_area_code", "")}){" "}
                        {get(item, "phone", "")}
                      </td>

                      <td className="td min-w-40">{lastLoginAt}</td>

                      <td className="td min-w-40">{createdAt}</td>

                      <td className="td min-w-40">{updatedAt}</td>

                      <td className="td min-w-[120px]">
                        {handleFormatCreatedBy(get(item, "created_by", ""))}
                      </td>

                      <td className="td py-4 pl-3 pr-4 sm:pr-6 lg:pr-8 flex items-center gap-2">
                        {isHaveActionPermission(
                          "",
                          "customer_accounts",
                          "view"
                        ) && (
                          <Button
                            className="w-8 h-8 bg-gray-200 hover:bg-gray-100 active:bg-gray-300"
                            onClick={() => {
                              navigate(
                                `/customer-accounts/detail/${get(
                                  item,
                                  "code",
                                  ""
                                )}`
                              );
                            }}
                          >
                            <EyeIcon className="w-4 h-4 text-black" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Pagination
        currentPage={get(paging, "current_page", 1)}
        totalPage={get(paging, "total_page", 0)}
        total={{
          totalItems: get(paging, "total_item", 0),
          total: get(paging, "total", 0),
        }}
        onChange={handleChangePage}
      />
    </section>
  );
};

export default DataTable;
